import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DollarSign, Calendar, Clock, TrendingUp, ChevronDown, ChevronRight, FileText, MapPin, ArrowRight } from 'lucide-react';
import { getLoadStatusClasses } from '@/lib/statusColors';

function getPayPeriod(date) {
  const d = new Date(date);
  const day = d.getDate();
  const month = d.getMonth();
  const year = d.getFullYear();
  if (day <= 15) {
    return {
      start: new Date(year, month, 1),
      end: new Date(year, month, 15),
      label: `${d.toLocaleString('default', { month: 'short' })} 1-15, ${year}`,
      payoutDate: new Date(year, month, 18),
    };
  }
  const lastDay = new Date(year, month + 1, 0).getDate();
  return {
    start: new Date(year, month, 16),
    end: new Date(year, month + 1, 0),
    label: `${d.toLocaleString('default', { month: 'short' })} 16-${lastDay}, ${year}`,
    payoutDate: new Date(year, month + 1, 3),
  };
}

function getCurrentPayPeriod() {
  return getPayPeriod(new Date());
}

function getPayPeriodKey(date) {
  const p = getPayPeriod(date);
  return p.label;
}

function formatCurrency(amount) {
  return `$${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(date) {
  return new Date(date).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' });
}

const INVOICE_STATUS_COLORS = {
  paid: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 border-gray-200 dark:border-gray-700',
};

function LoadRow({ load, invoice }) {
  return (
    <div className="flex items-center justify-between py-3 px-4 bg-surface rounded-xl border border-border hover:border-brand-accent/30 transition-colors">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold truncate">{load.cargo_description || 'Load'}</p>
          {invoice && (
            <Badge className={`text-[9px] border ${INVOICE_STATUS_COLORS[invoice.status] || INVOICE_STATUS_COLORS.draft}`}>
              {invoice.status?.toUpperCase()}
            </Badge>
          )}
        </div>
        <div className="flex items-center text-xs text-muted-foreground mt-1">
          <MapPin className="w-3 h-3 mr-1 shrink-0" />
          <span className="truncate">{load.pickup_name || load.pickup_address?.split(',')[0]}</span>
          <ArrowRight className="w-3 h-3 mx-1 shrink-0" />
          <span className="truncate">{load.delivery_name || load.delivery_address?.split(',')[0]}</span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
          <span>{formatDate(load.completed_at || load.updated_at || load.created_at)}</span>
          {invoice?.invoice_number && (
            <span className="flex items-center gap-1">
              <FileText className="w-3 h-3" />
              {invoice.invoice_number}
            </span>
          )}
        </div>
      </div>
      <div className="text-right ml-3">
        <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(load.rate)}</p>
        <Badge className={`text-[10px] border ${getLoadStatusClasses(load.status)}`}>
          {load.status?.toUpperCase()}
        </Badge>
      </div>
    </div>
  );
}

export default function DriverPayments() {
  const { token, user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPeriods, setExpandedPeriods] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [loadsRes, invoicesRes] = await Promise.all([
          api.get('/loads'),
          api.get('/invoices').catch(() => ({ data: [] })),
        ]);
        const completed = loadsRes.data.filter(
          l => ['completed', 'invoiced'].includes(l.status) && l.driver_id === user?.user_id
        );
        setLoads(completed);
        setInvoices(invoicesRes.data || []);
      } catch (err) {
        console.error('Failed to fetch payments data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token, user]);

  const invoiceMap = useMemo(() => {
    const map = {};
    invoices.forEach(inv => {
      if (inv.load_id) map[inv.load_id] = inv;
    });
    return map;
  }, [invoices]);

  const currentPeriod = getCurrentPayPeriod();
  const now = new Date();

  const currentPeriodLoads = useMemo(() => {
    return loads.filter(l => {
      const d = new Date(l.completed_at || l.updated_at || l.created_at);
      return d >= currentPeriod.start && d <= currentPeriod.end;
    });
  }, [loads, currentPeriod]);

  const totalEarned = useMemo(() => loads.reduce((sum, l) => sum + (l.rate || 0), 0), [loads]);
  const thisPeriodEarned = useMemo(() => currentPeriodLoads.reduce((sum, l) => sum + (l.rate || 0), 0), [currentPeriodLoads]);

  const pendingPayment = useMemo(() => {
    return loads
      .filter(l => {
        const inv = invoiceMap[l.load_id];
        return !inv || inv.status !== 'paid';
      })
      .reduce((sum, l) => sum + (l.rate || 0), 0);
  }, [loads, invoiceMap]);

  const lastPayout = useMemo(() => {
    const paidInvoices = invoices.filter(inv => inv.status === 'paid' && inv.amount);
    if (paidInvoices.length === 0) return 0;
    paidInvoices.sort((a, b) => new Date(b.paid_at || b.updated_at || b.created_at) - new Date(a.paid_at || a.updated_at || a.created_at));
    return paidInvoices[0].amount || 0;
  }, [invoices]);

  // Group loads by pay period for history
  const periodGroups = useMemo(() => {
    const groups = {};
    loads.forEach(l => {
      const key = getPayPeriodKey(l.completed_at || l.updated_at || l.created_at);
      if (!groups[key]) {
        const period = getPayPeriod(l.completed_at || l.updated_at || l.created_at);
        groups[key] = { ...period, loads: [] };
      }
      groups[key].loads.push(l);
    });
    // Sort by start date descending
    return Object.values(groups).sort((a, b) => b.start - a.start);
  }, [loads]);

  // Exclude current period from history
  const historyGroups = useMemo(() => {
    return periodGroups.filter(g => g.label !== currentPeriod.label);
  }, [periodGroups, currentPeriod]);

  const togglePeriod = (label) => {
    setExpandedPeriods(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // Progress bar calculations
  const paidAmount = totalEarned - pendingPayment;
  const progressTotal = totalEarned || 1;
  const paidPercent = (paidAmount / progressTotal) * 100;
  const pendingPercent = (pendingPayment / progressTotal) * 100;

  // Days until next payout
  const daysUntilPayout = Math.max(0, Math.ceil((currentPeriod.payoutDate - now) / (1000 * 60 * 60 * 24)));

  if (loading) {
    return (
      <div className="space-y-4">
        {Array(4).fill(0).map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-2xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="driver-payments">
      <h1 className="text-2xl font-bold tracking-tight">Payments & Earnings</h1>

      {/* Earnings Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-emerald-50 border-emerald-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{formatCurrency(totalEarned)}</p>
            <p className="text-[10px] text-emerald-500 dark:text-muted-foreground font-bold tracking-wider">TOTAL EARNED</p>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Calendar className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{formatCurrency(thisPeriodEarned)}</p>
            <p className="text-[10px] text-blue-500 dark:text-muted-foreground font-bold tracking-wider">THIS PERIOD</p>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{formatCurrency(pendingPayment)}</p>
            <p className="text-[10px] text-amber-500 dark:text-muted-foreground font-bold tracking-wider">PENDING</p>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200 dark:bg-card dark:border-border rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{formatCurrency(lastPayout)}</p>
            <p className="text-[10px] text-purple-500 dark:text-muted-foreground font-bold tracking-wider">LAST PAYOUT</p>
          </CardContent>
        </Card>
      </div>

      {/* Payment Terms Section */}
      <Card className="rounded-2xl border-border">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-bold">Bi-weekly Payments</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Period 1: 1st - 15th (paid ~18th) | Period 2: 16th - end of month (paid ~3rd)
              </p>
            </div>
            <Badge className="bg-brand-accent/10 text-brand-accent border-brand-accent/20 text-xs">
              {daysUntilPayout} days to payout
            </Badge>
          </div>

          <div>
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
              <span>Current: {currentPeriod.label}</span>
              <span>Next payout: {formatDate(currentPeriod.payoutDate)}</span>
            </div>
            <div className="h-3 bg-muted rounded-full overflow-hidden flex">
              <div
                className="bg-emerald-500 rounded-l-full transition-all duration-500"
                style={{ width: `${paidPercent}%` }}
                title={`Paid: ${formatCurrency(paidAmount)}`}
              />
              <div
                className="bg-amber-400 transition-all duration-500"
                style={{ width: `${pendingPercent}%` }}
                title={`Pending: ${formatCurrency(pendingPayment)}`}
              />
            </div>
            <div className="flex items-center gap-4 mt-2 text-[10px] text-muted-foreground">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500" /> Paid
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-400" /> Pending
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-muted" /> Remaining
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="current" className="space-y-4">
        <TabsList className="bg-muted/50 rounded-xl p-1">
          <TabsTrigger value="current" className="rounded-lg text-xs">Current Period</TabsTrigger>
          <TabsTrigger value="history" className="rounded-lg text-xs">History</TabsTrigger>
          <TabsTrigger value="all" className="rounded-lg text-xs">All Earnings</TabsTrigger>
        </TabsList>

        {/* Current Period Tab */}
        <TabsContent value="current" className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-muted-foreground">{currentPeriod.label}</h3>
            <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(thisPeriodEarned)}
            </span>
          </div>
          {currentPeriodLoads.length === 0 ? (
            <Card className="rounded-2xl border-border">
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                No completed loads in this pay period yet
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {currentPeriodLoads.map(load => (
                <LoadRow key={load.load_id} load={load} invoice={invoiceMap[load.load_id]} />
              ))}
            </div>
          )}
          <Card className="rounded-2xl border-border bg-surface">
            <CardContent className="p-4 flex items-center justify-between">
              <span className="text-sm font-semibold">Period Total</span>
              <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                {formatCurrency(thisPeriodEarned)}
              </span>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-3">
          {historyGroups.length === 0 ? (
            <Card className="rounded-2xl border-border">
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                No past pay periods yet
              </CardContent>
            </Card>
          ) : (
            historyGroups.map(group => {
              const isExpanded = expandedPeriods[group.label];
              const periodTotal = group.loads.reduce((sum, l) => sum + (l.rate || 0), 0);
              return (
                <Card key={group.label} className="rounded-2xl border-border overflow-hidden">
                  <button
                    onClick={() => togglePeriod(group.label)}
                    className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                      <div>
                        <p className="text-sm font-semibold">{group.label}</p>
                        <p className="text-xs text-muted-foreground">{group.loads.length} load{group.loads.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <span className="text-lg font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(periodTotal)}
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
                      {group.loads.map(load => (
                        <LoadRow key={load.load_id} load={load} invoice={invoiceMap[load.load_id]} />
                      ))}
                    </div>
                  )}
                </Card>
              );
            })
          )}
        </TabsContent>

        {/* All Earnings Tab */}
        <TabsContent value="all" className="space-y-2">
          {loads.length === 0 ? (
            <Card className="rounded-2xl border-border">
              <CardContent className="p-8 text-center text-muted-foreground text-sm">
                No completed loads yet
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">{loads.length} load{loads.length !== 1 ? 's' : ''}</span>
                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                  {formatCurrency(totalEarned)}
                </span>
              </div>
              <div className="space-y-2 max-h-[600px] overflow-auto pr-1">
                {loads
                  .sort((a, b) => new Date(b.completed_at || b.updated_at || b.created_at) - new Date(a.completed_at || a.updated_at || a.created_at))
                  .map(load => (
                    <LoadRow key={load.load_id} load={load} invoice={invoiceMap[load.load_id]} />
                  ))}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
